import React, { useEffect, useMemo, useState } from "react";
import { Flex, Icon, ItemType, Slider, Space, Text } from "@thenounproject/lingo-core";
import { Inspectable } from "@constants/Inspector";
import { DisplayProperties } from "@actions/items/useBatchSaveItems";
import { getSharedAttribute } from "./utils";
import { DesktopDown } from "@src/features/media-queries";

type SizeOption = {
  value: number;
  label: string;
};

type Props = {
  inspectables: Inspectable[];
  space: Space;
  onUpdateDisplayProps: (displayProperties: DisplayProperties, items?: Inspectable[]) => void;
};

export const DisplaySizeOptions: React.FC<Props> = ({ inspectables, onUpdateDisplayProps }) => {
  const [localDisplaySize, setLocalDisplaySize] = useState<number | null>(null);

  const sizableItems = useMemo(
    () =>
      inspectables.filter(i =>
        [
          ItemType.note,
          ItemType.asset,
          ItemType.gallery,
          ItemType.guide,
          ItemType.codeSnippet,
        ].includes(i.item?.type)
      ),
    [inspectables]
  );

  const sizeOptions = useMemo(
    () => [
      { value: 6, label: "Thumbnail" },
      { value: 5, label: "Small" },
      { value: 4, label: "Medium" },
      { value: 3, label: "Large" },
      { value: 2, label: "1/2" },
      { value: 1, label: "Full Width" },
    ],
    []
  );

  const displaySize = useMemo(
    () => getSharedAttribute(sizableItems, i => i.item?.displayProperties?.size),
    [sizableItems]
  );

  const effectiveDisplaySize = localDisplaySize ?? displaySize;

  useEffect(() => {
    setLocalDisplaySize(null);
  }, [displaySize]);

  const currentSizeOption = useMemo(
    () => sizeOptions.find(option => option.value === effectiveDisplaySize) || sizeOptions[0],
    [sizeOptions, effectiveDisplaySize]
  );

  if (sizableItems.length === 0) return null;

  return (
    <Flex key="size-options" flexDirection="column" gap="4px">
      <Slider
        values={sizeOptions}
        selectedValue={currentSizeOption}
        onChange={(selectedOption: SizeOption) => {
          // Update local state optimistically during drag
          setLocalDisplaySize(selectedOption.value);
        }}
        onChangeComplete={(selectedOption: SizeOption) => {
          const newSize = selectedOption.value as 1 | 2 | 3 | 4 | 5 | 6;
          onUpdateDisplayProps(
            {
              size: newSize,
            },
            inspectables
          );
        }}
      />
      <DesktopDown>
        <Flex gap="2px" alignItems="center" mb="m">
          <Icon iconId="info.warning" fill="warning" size="16" />
          <Text font="ui.tiny">Sizes may vary by canvas size.</Text>
        </Flex>
      </DesktopDown>
    </Flex>
  );
};
